import {
  ClockCircleOutlined,
  DollarCircleOutlined,
  FieldTimeOutlined,
  HeartOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import { useAppDispatch, useAppSelector } from 'hooks';
import { BillTypes, CurrencySymbol } from 'models';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { billAction } from 'redux/slices/billSlice';
import { moneyFormat } from 'utils';
import { Dispatch, memo, SetStateAction } from 'react';
interface IBillSelectorProps {
  setIsLayoutBillGroup: Dispatch<SetStateAction<boolean>>;
}

const BillSelector: React.FC<IBillSelectorProps> = ({ setIsLayoutBillGroup }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const billListByTable = useAppSelector((state) => state.bill.billListByTable);
  const shopInfo = useAppSelector((state) => state.shop.shopInfo);
  const loading = useAppSelector((state) => state.bill.loading);

  // Calculate offset for displaying time in correct timezone
  const offset = 9 - moment().utcOffset() / 60;

  const handleSelectBill = (bill: BillTypes) => {
    setIsLayoutBillGroup(false);
    dispatch(billAction.setBillInfo(bill));
  };

  const getAppointeeName = (bill: BillTypes) => {
    const appointees = bill.service_fee?.filter((i) => i.type === 1) || [];
    const names = appointees.map((i) => i?.user_of_shop?.nick_name).filter(Boolean);
    return names.length > 0 ? names.join(', ') : ' - ';
  };

  const stayTimeOfUnpaidBill = (bill: BillTypes) => {
    const diff = moment(bill.stayTime).diff(moment(bill?.created_at), 'minutes');
    let hour = Math.floor(diff / 60);
    let minute = diff % 60;

    if (diff < 0) {
      hour += 23;
      minute += 60;
    }
    return `${hour}${t('HOUR_MINI')}${minute}${t('MINUTE_MINI')}`;
  };

  const renderItemBill = (bill: BillTypes) => (
    <button
      key={bill.id}
      className="flex flex-col max-w-[200px] sm:w-[180px] w-full"
      onClick={() => handleSelectBill(bill)}
    >
      <div className="w-full min-h-[200px] rounded-lg border-8 border-primary bg-white">
        <div className="pb-1 flex flex-col items-center justify-center border-b-2 border-#D0D5DD pt-2">
          {bill.tables?.name || t('FREE')}
        </div>
        <div className="pb-1 flex flex-col items-center justify-center border-b-2 border-#D0D5DD">
          {bill.code}
        </div>
        <div className="px-4 pb-3">
          <div className="w-full text-sm flex mt-2">
            <ClockCircleOutlined style={{ fontSize: 16, color: '#999' }} />
            <span className="ml-2">
              {moment(bill?.created_at).add(offset, 'hours').format('HH:mm')}
            </span>
          </div>
          <div className="w-full text-sm flex mt-2">
            <UserOutlined style={{ fontSize: 16, color: '#999' }} />
            <span className="ml-2">{bill.service_fee?.length || 0}</span>
            <span className="ml-10">{getAppointeeName(bill)}</span>
          </div>
          <div className="w-full text-sm flex mt-2">
            <HeartOutlined style={{ fontSize: 16, color: '#999' }} />
            <span className="ml-2">{bill?.customer?.name || ' - '}</span>
          </div>
          <div className="w-full text-sm flex mt-2">
            <FieldTimeOutlined style={{ fontSize: 16, color: '#999' }} />
            <span className="ml-2">{stayTimeOfUnpaidBill(bill)}</span>
          </div>
          <div className="w-full text-sm flex mt-2">
            <DollarCircleOutlined style={{ fontSize: 16, color: '#999' }} />
            <span className="ml-2">
              {moneyFormat(bill?.total_payment || 0, shopInfo?.currency as CurrencySymbol)}
            </span>
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="w-full bg-white relative">
      <div className="grid-wrap">
        <Spin spinning={loading}>
          <div className="p-4 py-20 flex flex-col">
            <button
              onClick={() => setIsLayoutBillGroup(false)}
              className={`flex self-end flex-col items-center justify-center bg-white gap-y-1 font-[NotoSans] font-semibold text-primary text-xs text-center px-4 border border-solid border-primary rounded-lg py-3`}
            >
              {t('MENU')}
            </button>
            <div className="sm:flex sm:flex-wrap grid grid-cols-2 gap-3 mt-2">
              {billListByTable?.map(renderItemBill)}
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default memo(BillSelector);
